import Config from "../config/env";
import { triggerGlobalLogout } from "../utils/globalLogout";
import type { ZodError, ZodSchema } from "zod";

// Types for API service
export interface ApiServiceOptions {
  body?: unknown;
  replace?: Record<string, string>;
  token?: string;
  schema?: ZodSchema<unknown>;
}

// Enhanced error type with status information
export interface ApiError extends Error {
  status?: number;
  statusText?: string;
  url?: string;
}

// NOTE: UnauthorizedResponse interface removed as we now handle 401s
// via global logout events instead of URL-based redirection

let authToken: string | null = null;

const setAuthToken = (token: string): void => {
  authToken = token;
};

// NOTE: redirectToLoginIfUnauthorized function removed as we now use
// triggerGlobalLogout() to handle 401s via WorkOS signOut instead of URL redirection

const apiService = async <T = unknown>(
  url: string,
  method = "GET",
  options: ApiServiceOptions = {},
  signal?: AbortSignal,
): Promise<T> => {
  const defaultHeaders: Record<string, string> = {};

  if (!(options.body instanceof FormData)) {
    defaultHeaders["Content-Type"] = "application/json";
  }

  let requestUrl = url;
  if (options.replace) {
    for (const key of Object.keys(options.replace)) {
      requestUrl = requestUrl.replace(`{${key}}`, options.replace[key]);
    }
  }

  if (options.token) {
    defaultHeaders.Authorization = `Bearer ${options.token}`;
  } else if (authToken) {
    defaultHeaders.Authorization = `Bearer ${authToken}`;
  } else {
    console.warn("⚠️ No token provided for API request:", url);
  }

  // Validate request body if a Zod schema is provided
  if (options.schema) {
    try {
      const body = {
        ...(options.body && typeof options.body === "object"
          ? options.body
          : {}),
        ...(options.replace || {}),
      };
      options.schema.parse(body);
    } catch (error) {
      console.error("Validation failed:", (error as ZodError).errors);
      throw error;
    }
  }

  const config: RequestInit = {
    method,
    credentials: "include",
    headers: defaultHeaders,
    body:
      typeof options.body === "object" &&
      options.body !== null &&
      !(options.body instanceof FormData)
        ? JSON.stringify(options.body)
        : (options.body as BodyInit | null | undefined),
    signal,
  };

  try {
    const urlObj = new URL(requestUrl);

    // remove subscription key for now:
    // urlObj.searchParams.set(
    //   "subscription-key",
    //   Config.APIM_GATEWAY_SUBSCRIPTION_KEY,
    // );

    const response = await fetch(urlObj.toString(), config);
    let data: Record<string, unknown>;
    try {
      data = await response.json();
    } catch (parseError) {
      console.warn("Could not parse JSON from server:", parseError);
      data = {}; // fallback to empty object
    }

    if (!response.ok) {
      // Handle 401 Unauthorized responses
      if (response.status === 401) {
        // TODO: In the future, we should differentiate between different types of 401 responses:
        // - Token expired (most common case) -> logout user
        // - Insufficient permissions for specific resource -> show appropriate error
        // - Invalid token format -> logout user
        // For now, we assume token expiration and log out the user as this is the most common case

        console.log(
          "🔒 [Service] 401 Unauthorized - Token likely expired, triggering logout",
        );
        triggerGlobalLogout();

        // Return a response indicating unauthorized status without throwing error
        // This prevents Redux thunks from going to .rejected state during logout process
        return {
          ...data,
          unauthorized: true,
          loginUrl: "/login", // Keep for backward compatibility if needed
        } as T;
      }

      if (
        response.status === 403 &&
        data?.debugMessage ===
          "Error: You are unauthorized to perform this action"
      ) {
        window.location.href = "/forbidden";
      }

      // Create a more detailed error object with status code and message
      const errorObj = new Error(
        (data as { message?: string })?.message || "An error occurred",
      ) as ApiError;
      errorObj.status = response.status;
      errorObj.statusText = response.statusText;
      errorObj.url = requestUrl;
      throw errorObj;
    }
    return data as T;
  } catch (error) {
    // For network failures (like server down), add status code info
    const apiError = error as ApiError;
    if (
      !apiError.status &&
      apiError.name === "TypeError" &&
      apiError.message === "Failed to fetch"
    ) {
      apiError.status = 0;
      apiError.statusText = "Connection Error";
      apiError.message =
        "Cannot connect to the server. The server may be down or unreachable.";
    }

    console.error("Fetch error:", error);
    throw error;
  }
};

export { setAuthToken };

export default apiService;
